'use client';
import { InteractionType } from '@azure/msal-browser';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useEffect, useState, type ReactNode } from 'react';

import ErrorView from 'components/Error';
import Loading from 'components/Loading';
import { FullscreenCenter } from 'components/shared-styles-v2';
import { UNAUTHORIZED_USER_PATH } from 'config/internalUrlPaths';
import {
  setStoredAuthMethod,
  AUTH_METHOD,
  setStoredAuthToken,
  setSchoolRedirectUrl,
  getStoredAuthMethod,
  getStoredAuthToken,
  initCompassAuthToken,
} from 'context/Auth/auth-provider.utils';

import { getAccessToken, initialiseMsal, msalInstance } from './msal-utils';

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [authMethod, setAuthMethod] = useState<AUTH_METHOD | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  // Set while we route back from the sign-in redirect URI (the site root) to the page the sign-in started on.
  const [signInLandingPath, setSignInLandingPath] = useState<string | null>(null);

  // Ensure component only renders on client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const initializeAuth = async () => {
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(hash.substring(1));
      const schoolToken = urlParams.get('token');
      const isSchoolAuthMethod = getStoredAuthMethod() === AUTH_METHOD.SCHOOL;

      try {
        if (schoolToken) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          // Only save cross-origin referrers as the school portal redirect target.
          // Same-origin referrers come from internal openAuthenticatedTab calls and would
          // incorrectly hijack the post-logout redirect.
          const referrer = document.referrer;
          if (referrer && new URL(referrer).origin !== window.location.origin) {
            setSchoolRedirectUrl(referrer);
          }
          setStoredAuthToken(schoolToken);
          setStoredAuthMethod(AUTH_METHOD.SCHOOL);
          setAuthMethod(AUTH_METHOD.SCHOOL);
        } else if (isSchoolAuthMethod) {
          setAuthMethod(AUTH_METHOD.SCHOOL);
          const storedToken = getStoredAuthToken();
          if (!storedToken) {
            setIsUnauthorized(true);
          }
        } else {
          await initialiseMsal((path) => {
            if (new URL(path, window.location.origin).pathname !== window.location.pathname) {
              setSignInLandingPath(window.location.pathname);
            }
            router.replace(path);
          });
          const token = await getAccessToken();
          if (token) {
            setStoredAuthMethod(AUTH_METHOD.MSAL);
            setAuthMethod(AUTH_METHOD.MSAL);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (!authMethod) {
      initializeAuth();
    }
  }, [authMethod, isMounted, router]);

  useEffect(() => {
    if (signInLandingPath !== null && pathname !== signInLandingPath) {
      setSignInLandingPath(null);
    }
  }, [pathname, signInLandingPath]);

  // Once authenticated, eagerly mint the tf-compass token so it is ready before the
  // first Compass request. Safe to call repeatedly — minting is guarded internally.
  useEffect(() => {
    if (authMethod) {
      initCompassAuthToken();
    }
  }, [authMethod]);

  // Show loading until mounted on client, and until we've left the sign-in landing page: rendering the root page
  // there would start its own redirect to the dashboard
  if (!isMounted || isLoading || (signInLandingPath !== null && pathname === signInLandingPath)) {
    return (
      <div suppressHydrationWarning>
        <FullscreenCenter>
          <Loading />
        </FullscreenCenter>
      </div>
    );
  }

  if (isUnauthorized && pathname !== UNAUTHORIZED_USER_PATH) {
    router.push(UNAUTHORIZED_USER_PATH);
  }

  // School token authenticated - bypass MSAL
  if (authMethod === AUTH_METHOD.SCHOOL) {
    return <Fragment>{children}</Fragment>;
  }

  // MSAL authenticated or needs authentication
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        loadingComponent={() => (
          <FullscreenCenter>
            <Loading />
          </FullscreenCenter>
        )}
        errorComponent={({ error }) => <ErrorView error={error} />}
      >
        {children}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

export default AuthProvider;
